
import { supabase } from "@/lib/supabase";

/**
 * Creates a new notification for a user
 */
export const createNotification = async (
  userId: string, 
  content: string,
  type: 'follow' | 'message' | 'achievement' | 'opportunity' = 'follow'
): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('user_notifications')
      .insert({
        user_id: userId,
        content,
        type,
        is_read: false
      })
      .select('id')
      .single();
      
    if (error) throw error;
    
    return data.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    return null;
  }
};

/**
 * Creates a follow notification when one user follows another
 */
export const createFollowNotification = async (
  targetUserId: string,
  followerName: string
): Promise<string | null> => {
  const content = `${followerName} started following you`;
  return createNotification(targetUserId, content, 'follow');
};

/**
 * Creates a message notification when a user receives a message
 */
export const createMessageNotification = async (
  receiverId: string,
  senderName: string
): Promise<string | null> => {
  const content = `${senderName} sent you a message`;
  return createNotification(receiverId, content, 'message');
};

/**
 * Creates an achievement notification when a user completes a level
 */
export const createAchievementNotification = async (
  userId: string,
  level: number
): Promise<string | null> => {
  const content = `Congratulations! You've reached level ${level}`;
  return createNotification(userId, content, 'achievement');
};

/**
 * Creates an opportunity notification when a new opportunity is available
 */
export const createOpportunityNotification = async (
  userId: string,
  opportunityTitle: string
): Promise<string | null> => {
  const content = `New opportunity: ${opportunityTitle}`;
  return createNotification(userId, content, 'opportunity');
};

/**
 * Clears all notifications for a user
 */
export const clearAllNotifications = async (
  userId: string
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('user_notifications')
      .delete()
      .eq('user_id', userId);
      
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Error clearing notifications:', error);
    return false;
  }
};

/**
 * Marks a notification as read
 */
export const markNotificationAsRead = async (
  notificationId: string
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('user_notifications')
      .update({ is_read: true })
      .eq('id', notificationId);
      
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return false;
  }
};
